import type { LanguageCode } from './languages.ts';

/**
 * Voix exposées dans l'interface. L'identifiant est celui attendu par le service IA :
 * voix Kokoro-82M (Apache 2.0), sauf le français, lu par Pocket TTS de Kyutai (poids CC-BY 4.0 ;
 * voix intégrées sous CC0 ou CC-BY 4.0, utilisables commercialement).
 */
export interface Voice {
  id: string;
  name: string;
  language: LanguageCode;
  accent?: string;
  gender: 'female' | 'male';
}

export const VOICES: Voice[] = [
  { id: 'af_heart', name: 'Heart', language: 'en', accent: 'US', gender: 'female' },
  { id: 'af_bella', name: 'Bella', language: 'en', accent: 'US', gender: 'female' },
  { id: 'af_nicole', name: 'Nicole', language: 'en', accent: 'US', gender: 'female' },
  { id: 'af_sarah', name: 'Sarah', language: 'en', accent: 'US', gender: 'female' },
  { id: 'am_michael', name: 'Michael', language: 'en', accent: 'US', gender: 'male' },
  { id: 'am_fenrir', name: 'Fenrir', language: 'en', accent: 'US', gender: 'male' },
  { id: 'am_puck', name: 'Puck', language: 'en', accent: 'US', gender: 'male' },
  { id: 'bf_emma', name: 'Emma', language: 'en', accent: 'UK', gender: 'female' },
  { id: 'bf_isabella', name: 'Isabella', language: 'en', accent: 'UK', gender: 'female' },
  { id: 'bm_george', name: 'George', language: 'en', accent: 'UK', gender: 'male' },
  { id: 'bm_fable', name: 'Fable', language: 'en', accent: 'UK', gender: 'male' },
  { id: 'fr_estelle', name: 'Estelle', language: 'fr', gender: 'female' },
  { id: 'fr_fantine', name: 'Fantine', language: 'fr', gender: 'female' },
  { id: 'fr_charles', name: 'Charles', language: 'fr', gender: 'male' },
  { id: 'fr_paul', name: 'Paul', language: 'fr', gender: 'male' },
  { id: 'fr_javert', name: 'Javert', language: 'fr', gender: 'male' },
  { id: 'ef_dora', name: 'Dora', language: 'es', gender: 'female' },
  { id: 'em_alex', name: 'Alex', language: 'es', gender: 'male' },
  { id: 'if_sara', name: 'Sara', language: 'it', gender: 'female' },
  { id: 'im_nicola', name: 'Nicola', language: 'it', gender: 'male' },
  { id: 'pf_dora', name: 'Dora', language: 'pt', accent: 'BR', gender: 'female' },
  { id: 'pm_alex', name: 'Alex', language: 'pt', accent: 'BR', gender: 'male' },
  { id: 'hf_alpha', name: 'Alpha', language: 'hi', gender: 'female' },
  { id: 'hm_omega', name: 'Omega', language: 'hi', gender: 'male' },
];

/** Voix retirées du choix, encore reconnues pour relancer un ancien projet. */
const RETIRED_VOICES: Voice[] = [{ id: 'ff_siwis', name: 'Siwis', language: 'fr', gender: 'female' }];

export const VOICE_IDS = [...VOICES, ...RETIRED_VOICES].map((v) => v.id) as [string, ...string[]];

export function voicesFor(language: string): Voice[] {
  return VOICES.filter((v) => v.language === language);
}

export function findVoice(id: string): Voice | undefined {
  return VOICES.find((v) => v.id === id) ?? RETIRED_VOICES.find((v) => v.id === id);
}
